// 1. Imports React
import { useEffect, useMemo, useRef, useState } from "react";

// 2. Imports externes
import { format } from "date-fns";
import { fr } from "date-fns/locale";

// 3. Imports composants UI
import { Badge } from "./ui/badge";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import WorSessionFormDialog from "./forms/WorSessionFormDialog";

// 4. Imports hooks personnalisés
import {
  useDeleteWorkSession,
  useFetchWorkSessions,
} from "@/hooks/useWorkSessions";

// 5. Imports types
import type { WorkSession } from "@/types/workSession.type";

// 6. Imports utils
import {
  formatDuration,
  getStartOfDayUTC,
  getEndOfDayUTC,
} from "@/lib/date.utils";

import {
  UserSessionsFiltersBar,
  type UserSessionsFilterType,
} from "./sessions/UserSessionsFiltersBar";
import { UserSessionsGroupedList } from "./sessions/UserSessionsGroupedList";

// 7. Types et interfaces
export interface UserSessionsStats {
  totalHours: string;
  totalSessions: number;
  averageDaily: string;
  sessionsThisMonth: number;
}

export interface UserSessionsLabels {
  totalHours: string;
  sessions: string;
  average: string;
  subtitle: string;
}

interface UserSessionsProps {
  userId: string;
  userName?: string; // Nom complet affiché (ex. "Jean Dupont")
  firstName?: string;
  lastName?: string;
  // Callback pour remonter les stats et labels au parent
  onStatsChange?: (
    stats: UserSessionsStats,
    labels: UserSessionsLabels
  ) => void;
}

// 8. Composant
const UserSessions = ({
  userId,
  userName,
  firstName,
  lastName,
  onStatsChange,
}: UserSessionsProps) => {
  // ========== A. Hooks React ==========
  const [filterType, setFilterType] =
    useState<UserSessionsFilterType>("all");
  const [selectedDay, setSelectedDay] = useState<Date>();
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [periodStart, setPeriodStart] = useState<Date>();
  const [periodEnd, setPeriodEnd] = useState<Date>();
  const [dayPopoverOpen, setDayPopoverOpen] = useState(false);
  const [monthPopoverOpen, setMonthPopoverOpen] = useState(false);
  const [periodStartPopoverOpen, setPeriodStartPopoverOpen] = useState(false);
  const [periodEndPopoverOpen, setPeriodEndPopoverOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [editingSession, setEditingSession] = useState<WorkSession | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [openWorSessionFormDialog, setOpenWorSessionFormDialog] =
    useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [workSessionToDelete, setWorkSessionToDelete] =
    useState<WorkSession | null>(null);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  // État pour gérer quelles dates sont ouvertes/fermées
  const [openDates, setOpenDates] = useState<Set<string>>(new Set());

  // ========== B. Custom Hooks ==========
  const { mutate: deleteSessionMutation, isPending: isDeleting } =
    useDeleteWorkSession();

  // ========== C. Fonctions utilitaires ==========
  // Les fonctions de gestion de dates sont maintenant importées depuis @/lib/date.utils

  // Calculer la plage de dates selon le filtre actif
  const getDateRange = () => {
    if (filterType === "all") {
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        startDate: getStartOfDayUTC(firstDayOfMonth),
        endDate: getEndOfDayUTC(lastDayOfMonth),
      };
    }

    if (filterType === "day") {
      if (selectedDay) {
        const startOfDay = getStartOfDayUTC(selectedDay);
        const endOfDay = getEndOfDayUTC(selectedDay);
        return {
          startDate: startOfDay,
          endDate: endOfDay,
        };
      }
      // Si aucun jour n'est sélectionné, retourner le mois actuel
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        startDate: getStartOfDayUTC(firstDayOfMonth),
        endDate: getEndOfDayUTC(lastDayOfMonth),
      };
    }

    if (filterType === "month") {
      if (selectedMonth) {
        const firstDayOfMonth = new Date(
          selectedMonth.getFullYear(),
          selectedMonth.getMonth(),
          1
        );
        const lastDayOfMonth = new Date(
          selectedMonth.getFullYear(),
          selectedMonth.getMonth() + 1,
          0
        );

        return {
          startDate: getStartOfDayUTC(firstDayOfMonth),
          endDate: getEndOfDayUTC(lastDayOfMonth),
        };
      }
      // Si aucun mois n'est sélectionné, retourner le mois actuel
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        startDate: getStartOfDayUTC(firstDayOfMonth),
        endDate: getEndOfDayUTC(lastDayOfMonth),
      };
    }

    if (filterType === "period") {
      if (periodStart && periodEnd) {
        return {
          startDate: getStartOfDayUTC(periodStart),
          endDate: getEndOfDayUTC(periodEnd),
        };
      }
      // Si les dates ne sont pas sélectionnées, retourner le mois actuel
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        startDate: getStartOfDayUTC(firstDayOfMonth),
        endDate: getEndOfDayUTC(lastDayOfMonth),
      };
    }

    // Fallback : retourner le mois actuel (ne devrait jamais arriver)
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: getStartOfDayUTC(firstDayOfMonth),
      endDate: getEndOfDayUTC(lastDayOfMonth),
    };
  };

  // Vérifier s'il y a des filtres actifs
  const hasActiveFilters =
    filterType !== "all" ||
    selectedDay !== undefined ||
    (selectedMonth &&
      (selectedMonth.getMonth() !== new Date().getMonth() ||
        selectedMonth.getFullYear() !== new Date().getFullYear())) ||
    periodStart !== undefined ||
    periodEnd !== undefined;

  // ========== D. Valeurs calculées ==========
  const dateRange = getDateRange();

  // Récupérer les sessions de travail
  const { data: workSessionsData, isLoading: workSessionsLoading } =
    useFetchWorkSessions({
      userId: userId,
      ...dateRange,
      page: currentPage,
      limit: itemsPerPage,
    });

  const workSessions = workSessionsData?.workSessions || [];
  const pagination = workSessionsData?.pagination;
  const serverStats = workSessionsData?.stats;

  // Calculer les stats à partir des données du serveur
  const stats = useMemo(() => {
    if (!serverStats) {
      return {
        totalHours: "0h 0m",
        totalSessions: 0,
        averageDaily: "0h 0m",
        sessionsThisMonth: 0,
      };
    }

    // Formater le total d'heures
    const hours = Math.floor(serverStats.totalHours);
    const minutes = Math.round((serverStats.totalHours - hours) * 60);
    const totalHoursFormatted = `${hours}h ${minutes}m`;

    // Formater la moyenne par jour
    const avgH = Math.floor(serverStats.averageDaily);
    const avgM = Math.round((serverStats.averageDaily - avgH) * 60);
    const averageDaily = `${avgH}h ${avgM}m`;

    // Sessions ce mois : utiliser totalSessions si on filtre par mois actuel
    const now = new Date();
    const isCurrentMonthFilter =
      filterType === "all" ||
      (filterType === "month" &&
        selectedMonth &&
        selectedMonth.getMonth() === now.getMonth() &&
        selectedMonth.getFullYear() === now.getFullYear());

    const sessionsThisMonth = isCurrentMonthFilter
      ? serverStats.totalSessions
      : workSessions.filter((session) => {
          const sessionDate = new Date(session.date);
          return (
            sessionDate.getMonth() === now.getMonth() &&
            sessionDate.getFullYear() === now.getFullYear()
          );
        }).length;

    return {
      totalHours: totalHoursFormatted,
      totalSessions: serverStats.totalSessions,
      averageDaily: averageDaily,
      sessionsThisMonth: sessionsThisMonth,
    };
  }, [serverStats, workSessions, filterType, selectedMonth]);

  // Obtenir les labels dynamiques selon le filtre
  const labels = useMemo(() => {
    switch (filterType) {
      case "day":
        return {
          totalHours: selectedDay
            ? `Total heures (${format(selectedDay, "d MMMM", { locale: fr })})`
            : "Total heures (ce jour)",
          sessions: selectedDay
            ? `Sessions (${format(selectedDay, "d MMMM", { locale: fr })})`
            : "Sessions (ce jour)",
          average: "Moyenne (ce jour)",
          subtitle: "Pour ce jour",
        };

      case "month":
        return {
          totalHours: selectedMonth
            ? `Total heures (${format(selectedMonth, "MMMM yyyy", {
                locale: fr,
              })})`
            : "Total heures (ce mois)",
          sessions: selectedMonth
            ? `Sessions (${format(selectedMonth, "MMMM yyyy", { locale: fr })})`
            : "Sessions (ce mois)",
          average: "Moyenne / jour",
          subtitle: selectedMonth
            ? `Pour ${format(selectedMonth, "MMMM yyyy", { locale: fr })}`
            : "Pour ce mois",
        };

      case "period":
        return {
          totalHours:
            periodStart && periodEnd
              ? `Total heures (${format(periodStart, "d MMM", {
                  locale: fr,
                })} - ${format(periodEnd, "d MMM yyyy", { locale: fr })})`
              : "Total heures (cette période)",
          sessions:
            periodStart && periodEnd
              ? `Sessions (${format(periodStart, "d MMM", {
                  locale: fr,
                })} - ${format(periodEnd, "d MMM yyyy", { locale: fr })})`
              : "Sessions (cette période)",
          average: "Moyenne / jour",
          subtitle:
            periodStart && periodEnd
              ? `Du ${format(periodStart, "d MMM", { locale: fr })} au ${format(
                  periodEnd,
                  "d MMM yyyy",
                  { locale: fr }
                )}`
              : "Pour cette période",
        };

      default: // "all" ou mois actuel
        return {
          totalHours: "Total heures (ce mois)",
          sessions: "Sessions (ce mois)",
          average: "Moyenne / jour",
          subtitle: "Pour ce mois",
        };
    }
  }, [filterType, selectedDay, selectedMonth, periodStart, periodEnd]);

  // Helper pour parser une date YYYY-MM-DD en date locale (pas UTC)
  const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  // Fonction pour ouvrir/fermer une date
  const toggleDate = (date: string) => {
    setOpenDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };


  // Grouper les sessions par date
  const groupedSessions = useMemo(() => {
    if (!workSessions || workSessions.length === 0) return [];

    const grouped = workSessions.reduce((acc, session) => {
      const dateKey = format(new Date(session.startTime), "yyyy-MM-dd");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(session);
      return acc;
    }, {} as Record<string, WorkSession[]>);

    return Object.entries(grouped)
      .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
      .map(([date, sessions]) => {
        // Calculer le total d'heures pour ce jour
        const totalHoursForDay = sessions.reduce(
          (sum, s) => sum + (s.duration || 0),
          0
        );

        // Trier les sessions par heure de début (plus tôt en premier)
        const sortedSessions = sessions.sort(
          (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );

        return {
          date,
          dateFormatted: format(parseLocalDate(date), "EEEE dd MMMM yyyy", {
            locale: fr,
          }),
          sessions: sortedSessions,
          totalHours: totalHoursForDay,
          sessionCount: sortedSessions.length,
        };
      });
  }, [workSessions]);

  // ========== E. Effects ==========
  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, selectedDay, selectedMonth, periodStart, periodEnd]);

  // Mettre à jour les stats et labels au parent
  const prevStatsRef = useRef<UserSessionsStats | null>(null);
  const prevLabelsRef = useRef<UserSessionsLabels | null>(null);

  useEffect(() => {
    if (!onStatsChange) return;

    const statsChanged =
      !prevStatsRef.current ||
      prevStatsRef.current.totalHours !== stats.totalHours ||
      prevStatsRef.current.totalSessions !== stats.totalSessions ||
      prevStatsRef.current.averageDaily !== stats.averageDaily ||
      prevStatsRef.current.sessionsThisMonth !== stats.sessionsThisMonth;

    const labelsChanged =
      !prevLabelsRef.current ||
      prevLabelsRef.current.totalHours !== labels.totalHours ||
      prevLabelsRef.current.sessions !== labels.sessions ||
      prevLabelsRef.current.average !== labels.average ||
      prevLabelsRef.current.subtitle !== labels.subtitle;

    if (statsChanged || labelsChanged) {
      prevStatsRef.current = stats;
      prevLabelsRef.current = labels;
      onStatsChange(stats, labels);
    }
  }, [stats, labels, onStatsChange]);

  // ========== F. Handlers ==========
  // Commencer à modifier une session de travail
  const handleEdit = (session: WorkSession) => {
    setOpenWorSessionFormDialog(false);
    setEditingSession(session);
  };

  // Ouvrir le dialog de création de session
  const handleOpenWorSessionFormDialog = (employeeId: string, date?: Date) => {
    setEditingSession(null);
    setEmployeeId(employeeId);
    setSelectedDate(date);
    setOpenWorSessionFormDialog(true);
  };

  // Fermer le dialog de création de session
  const handleCloseWorSessionFormDialog = () => {
    setOpenWorSessionFormDialog(false);
    setEditingSession(null);
    setEmployeeId(null);
    setSelectedDate(undefined);
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilterType("all");
    setSelectedDay(undefined);
    setSelectedMonth(
      new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );
    setPeriodStart(undefined);
    setPeriodEnd(undefined);
  };

  // Gérer le clic sur le bouton supprimer
  const handleDeleteClick = (session: WorkSession) => {
    setIsDeleteDialogOpen(true);
    setWorkSessionToDelete(session);
  };

  // Confirmer la suppression d'une session
  const handleConfirmDelete = () => {
    if (workSessionToDelete) {
      deleteSessionMutation(workSessionToDelete.id, {
        onSuccess: () => {
          setIsDeleteDialogOpen(false);
          setWorkSessionToDelete(null);
        },
      });
    }
  };

  // ========== G. JSX ==========
  return (
    <div className="space-y-4">
      {/* En-tête avec titre et badge */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h3 className="text-base sm:text-lg font-semibold">
            Sessions de pointage
          </h3>
          {userName && (
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              {userName}
            </p>
          )}
        </div>
        <Badge variant="secondary" className="w-fit">
          {workSessions?.length} sessions
        </Badge>
      </div>

      {/* Card avec filtres */}
      <div className="rounded-lg border bg-card p-3 sm:p-4">
        <UserSessionsFiltersBar
          filterType={filterType}
          onFilterTypeChange={setFilterType}
          selectedDay={selectedDay}
          onSelectedDayChange={setSelectedDay}
          dayPopoverOpen={dayPopoverOpen}
          onDayPopoverOpenChange={setDayPopoverOpen}
          selectedMonth={selectedMonth}
          onSelectedMonthChange={(month) => {
            if (month) setSelectedMonth(month);
          }}
          monthPopoverOpen={monthPopoverOpen}
          onMonthPopoverOpenChange={setMonthPopoverOpen}
          periodStart={periodStart}
          periodEnd={periodEnd}
          onPeriodStartChange={setPeriodStart}
          onPeriodEndChange={setPeriodEnd}
          periodStartPopoverOpen={periodStartPopoverOpen}
          onPeriodStartPopoverOpenChange={setPeriodStartPopoverOpen}
          periodEndPopoverOpen={periodEndPopoverOpen}
          onPeriodEndPopoverOpenChange={setPeriodEndPopoverOpen}
          hasActiveFilters={hasActiveFilters}
          onResetFilters={resetFilters}
          onAddSession={() => handleOpenWorSessionFormDialog(userId)}
        />

        <WorSessionFormDialog
          open={openWorSessionFormDialog || !!editingSession}
          onOpenChange={(open) => {
            if (!open) handleCloseWorSessionFormDialog();
          }}
          employeeId={editingSession?.employeeId ?? employeeId ?? userId}
          sessionToEdit={editingSession}
          onSuccess={handleCloseWorSessionFormDialog}
          date={editingSession ? undefined : selectedDate}
        />
      </div>

      <UserSessionsGroupedList
        loading={workSessionsLoading}
        groupedSessions={groupedSessions}
        openDates={openDates}
        onToggleDate={toggleDate}
        userId={userId}
        userName={userName}
        firstName={firstName}
        lastName={lastName}
        onAddSessionForDate={(date) =>
          handleOpenWorSessionFormDialog(userId, date)
        }
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newSize) => {
          setItemsPerPage(newSize);
          setCurrentPage(1);
        }}
      />

      {/* Dialog pour confirmer la suppression */}
      <ConfirmDeleteDialog
        title="Supprimer la session de travail"
        description="Êtes-vous sûr de vouloir supprimer cette session de travail ? Cette action est irréversible."
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        sessionDate={
          workSessionToDelete
            ? format(new Date(workSessionToDelete.startTime), "PPP", {
                locale: fr,
              })
            : undefined
        }
        employeeName={
          workSessionToDelete?.user?.firstName &&
          workSessionToDelete?.user?.lastName
            ? `${workSessionToDelete.user.firstName} ${workSessionToDelete.user.lastName}`
            : workSessionToDelete?.user?.firstName ||
              workSessionToDelete?.user?.lastName ||
              ""
        }
        sessionDuration={formatDuration(workSessionToDelete?.duration || 0)}
      />
    </div>
  );
};

export default UserSessions;
